<script setup lang="ts">
import { computed, type ComputedRef, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import gettext from '@/i18n/gettext';
import useAuthStore from '@/store/AuthStore';
import useCommonStore from '@/store/CommonStore';
import { Permission } from '@/models/Roles';

const { $gettext } = gettext;
const authStore = useAuthStore();
const commonStore = useCommonStore();
const { smAndDown } = useDisplay();

interface MainNavigationItem {
  text: string;
  link: string;
  icon: string;
  permission?: Permission;
}

interface MainNavigationItems {
  user: MainNavigationItem[];
  info: MainNavigationItem[];
  internal: MainNavigationItem[];
  devauge: MainNavigationItem[];
  admin: MainNavigationItem[];
}

const refreshKey = ref(0);

const navListItems: ComputedRef<MainNavigationItems> = computed(
  (): MainNavigationItems => {
    return {
      user: [
        {
          text: $gettext('Robot Finder'),
          link: '/',
          icon: 'mdi-calculator',
          permission: Permission.GET_CALCULATOR,
        },
        {
          text: $gettext('Robot Visualizer'),
          link: '/visualizer',
          icon: 'mdi-robot-industrial',
        },
        {
          text: $gettext('Fleet Management'),
          link: '/fleet-management',
          icon: 'mdi-factory',
        },
        {
          text: $gettext('Cloud RobFlow'),
          link: '/cloud-robflow',
          icon: 'mdi-code-block-tags',
          permission: Permission.GET_CLOUD_ROBFLOW,
        },
        {
          text: $gettext('RobVision'),
          link: '/robvision',
          icon: 'mdi-camera-outline',
          permission: Permission.GET_ROBVISION,
        },
        {
          text: $gettext('Metrics'),
          link: '/metrics',
          icon: 'mdi-chart-histogram',
          permission: Permission.GET_OEE_METRICS,
        },
      ],
      info: [
        {
          text: $gettext("How it's made"),
          link: '/how-its-made',
          icon: 'mdi-crosshairs-question',
          permission: Permission.GET_NPS,
        },
        {
          text: $gettext('Resources'),
          link: '/resources',
          icon: 'mdi-text-box-multiple',
        },
        {
          text: $gettext('Tutorials'),
          link: '/tutorials',
          icon: 'mdi-school-outline',
        },
        {
          text: $gettext('Help'),
          link: '/help',
          icon: 'mdi-help-circle',
        },
      ],
      internal: [
        {
          text: $gettext('Catalogue'),
          link: '/catalogue',
          icon: 'mdi-book-open-variant',
          permission: Permission.GET_CATALOGUE,
        },
        {
          text: $gettext('Custom Fleet Management'),
          link: '/internal/fleet',
          icon: 'mdi-factory',
          permission: Permission.INTERNAL_ACCESS,
        },
      ],
      devauge: [
        {
          text: $gettext('Palletizing status'),
          link: '/tangermunde-palletizing',
          icon: 'mdi-warehouse',
          permission: Permission.GET_DEVAUGE_TANGERMUNDE_VARIABLES,
        },
        {
          text: $gettext('Palletizing status'),
          link: '/luneburg-palletizing',
          icon: 'mdi-warehouse',
          permission: Permission.GET_DEVAUGE_LUNEBURG_VARIABLES,
        },
      ],
      admin: [
        {
          text: $gettext('NPS'),
          link: '/admin/nps',
          icon: 'mdi-comment-account-outline',
          permission: Permission.GET_NPS,
        },
        {
          text: $gettext('Users'),
          link: '/admin/users',
          icon: 'mdi-account-multiple-outline',
          permission: Permission.GET_USER,
        },
        {
          text: $gettext('Robots'),
          link: '/admin/control-units',
          icon: 'mdi-robot',
          permission: Permission.GET_CONTROL_UNIT,
        },
        {
          text: $gettext('Edge Devices'),
          link: '/admin/edge-devices',
          icon: 'mdi-developer-board',
          permission: Permission.GET_EDGE_DEVICE,
        },
        {
          text: $gettext('Customers'),
          link: '/admin/customers',
          icon: 'mdi-domain',
          permission: Permission.GET_CUSTOMER,
        },
      ],
    };
  },
);

const filteredNavListItems: ComputedRef<MainNavigationItems> = computed(() => {
  const result: MainNavigationItems = {
    user: [],
    info: [],
    internal: [],
    devauge: [],
    admin: [],
  };

  (Object.keys(navListItems.value) as (keyof MainNavigationItems)[]).forEach(
    (key) => {
      result[key] = navListItems.value[key].filter(
        (item: MainNavigationItem) =>
          !item.permission || authStore.hasPermission(item.permission),
      );
    },
  );
  return result;
});

watch(
  () => filteredNavListItems.value,
  () => {
    refreshKey.value += 1;
  },
);
</script>

<template>
  <v-navigation-drawer
    :key="refreshKey"
    class="main-navigation"
    :app="true"
    :model-value="smAndDown ? commonStore.forceMainNavigationDrawer : true"
    :expand-on-hover="!smAndDown"
    :rail="!smAndDown"
    rail-width="64"
    :permanent="!smAndDown"
    :overlay="smAndDown"
  >
    <v-list nav>
      <v-list-item
        v-for="(item, i) in filteredNavListItems.user"
        :key="i"
        class="mt-3"
        :prepend-icon="item.icon"
        :title="item.text"
        :to="item.link"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />

      <v-divider v-if="filteredNavListItems.info.length > 0" />
      <v-list-item
        v-for="(item, i) in filteredNavListItems.info"
        :key="i"
        class="mt-3"
        :prepend-icon="item.icon"
        :title="item.text"
        :to="item.link"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />

      <v-divider v-if="filteredNavListItems.info.length > 0" />
      <v-list-item
        v-for="(item, i) in filteredNavListItems.internal"
        :key="i"
        class="mt-3"
        :prepend-icon="item.icon"
        :title="item.text"
        :to="item.link"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />

      <v-divider v-if="filteredNavListItems.devauge.length > 0" />
      <v-list-item
        v-for="(item, i) in filteredNavListItems.devauge"
        :key="i"
        class="mt-3"
        :prepend-icon="item.icon"
        :title="item.text"
        :to="item.link"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />

      <v-divider v-if="filteredNavListItems.admin.length > 0" />
      <v-list-item
        v-for="(item, i) in filteredNavListItems.admin"
        :key="i"
        class="mt-3"
        :prepend-icon="item.icon"
        :title="item.text"
        :to="item.link"
        @click="commonStore.toggleForceMainNavigationDrawer"
      />
    </v-list>
  </v-navigation-drawer>
</template>
<style lang="scss">
.main-navigation {
  background-color: white !important;
  .v-navigation-drawer__content {
    overflow: scroll; /* Ensure content is scrollable */
    scrollbar-width: none; /* For Firefox */
    -ms-overflow-style: none; /* For Internet Explorer and Edge */

    /* Hide scrollbar for WebKit browsers (Chrome, Safari, Edge) */
    &::-webkit-scrollbar {
      display: none;
    }

    .v-list-item {
      padding-left: 10px;
    }
  }
}
</style>
